import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import {
  useCommissioning,
  useDeviceChecks,
  type CommissionedDevice,
} from '@/features/commissioning/useCommissioning';
import { SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import { CheckRow } from '@/features/provisioning/GoLivePanel';
import { AssignDeviceDialog } from '@/features/provisioning/AssignDeviceDialog';
import { useCommandClearances } from '@/features/clearance/useClearance';
import { ClearanceCell } from '@/features/clearance/ClearanceCell';
import { ClearForCommandingDialog } from '@/features/clearance/ClearForCommandingDialog';
import { WithdrawClearanceDialog } from '@/features/clearance/WithdrawClearanceDialog';

/**
 * THE BENCH — every commissioned tracker no customer owns yet (slice 18f).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN IS FOR. Mahad: *"I want to see the device up and reporting
 * before I assign to vehicle."*
 * ─────────────────────────────────────────────────────────────────────────────
 * A tracker is powered on at the bench, watched for a day, and only then sold.
 * Before 18f there was nowhere to watch it: a device entered Traccar only at
 * provisioning, which needs a tenant, a vehicle and a plate.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THREE STATES THAT MUST NEVER COLLAPSE INTO EACH OTHER.
 * ─────────────────────────────────────────────────────────────────────────────
 *   1. **Commissioned, no fix yet.** The row appears with `—` for position and
 *      says so in words. NO MAP MARKER — a dot at (0,0) or at the map's centre
 *      would be a coordinate nobody measured. This is the state Mahad watches
 *      while a unit warms up, so it has to read as WAITING, not as broken.
 *   2. **Reporting.** Marker plus a full row.
 *   3. **`degraded: true` — WE ARE BLIND.** Redis could not be read, so the API
 *      returns every device with a null position and `motion: 'offline'`. That
 *      is byte-for-byte a bench of dead trackers. Rendering it as "all stopped"
 *      is the worst available failure for this screen, so the blindness is
 *      stated in a banner and the motion column says nothing it cannot know.
 *
 * SUPER-ADMIN ONLY, GATED HERE AND NOT ONLY IN THE NAV. `GET
 * /admin/commissioning` carries IMEIs and is `requireSuperAdmin`; a deep link by
 * an `admin_staff` would render a screen whose every fetch 403s.
 *
 * NO ARTBOARD EXISTS FOR THIS SCREEN. Canvas `1u` draws the device register and
 * `3d` draws installations & inventory; neither draws a commissioning bench,
 * because the step did not exist when the canvas was drawn. The layout is `/live`
 * (map + list) and the table furniture is the shipped registers — stated rather
 * than approximated from a pixel source that does not exist.
 */
const GlobalMap = lazy(() =>
  import('@/features/live/GlobalMap').then((m) => ({ default: m.GlobalMap })),
);

const MOTION_DOT: Record<CommissionedDevice['motion'], string> = {
  moving: 'bg-emerald-500',
  idle: 'bg-amber-500',
  stopped: 'bg-slate-400',
  unknown: 'bg-indigo-500',
  offline: 'bg-slate-500',
};

/**
 * SLICE 14a — the hardening column.
 *
 * 🔴 FIVE STATES, THREE MEANINGS, AND THE DISTINCTION IS THE POINT.
 *   `no_command_channel` — an osmand phone. It can NEVER be hardened, so it is
 *      rendered as a quiet dash, not a warning. Marking it red would put a
 *      permanent alarm on two of five devices and teach the operator that the
 *      column is noise.
 *   `unhardened` / `secret_set` — a real tracker still reachable by SMS from any
 *      number, or half-way through. These are the ones to act on.
 *   `hardened` — done, and it means a benign command round-tripped through the
 *      new credential, not merely that we pushed one.
 *
 * No control is rendered here. The harden action is a super-admin write to a
 * customer's physical hardware and lives behind its own confirmation, not behind
 * a button in a table row that a mis-click can fire.
 */
function HardeningCell({ state }: { state: CommissionedDevice['hardening'] }) {
  const { t } = useTranslation();
  if (state === 'no_command_channel') {
    // 🔴 `text-raad-muted`, NOT `text-raad-dim`. The admin stylesheet declares
    // eleven raad tokens and `dim` is not among them — it is a WEB token. Using it
    // here compiles, lints, renders, and produces a dash with NO COLOUR, which is
    // exactly the defect Mahad found by eye in 24e (error text colourless on two
    // screens). Caught by ci/check-tailwind-tokens.mjs, which exists because of it.
    return <span className="text-raad-muted">—</span>;
  }
  const tone =
    state === 'hardened'
      ? 'text-raad-primary'
      : state === 'secret_set'
        ? 'text-raad-warn'
        : 'text-raad-error';
  return <span className={tone}>{t(`commissioning.hardening.${state}`)}</span>;
}

/** Whole days between two instants, floored, never negative. */
function daysSince(iso: string, nowMs: number): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((nowMs - then) / 86_400_000));
}

export function Commissioning() {
  const { t, i18n } = useTranslation();
  const { state } = useStaffAuth();
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const q = useCommissioning(isSuperAdmin);
  const checks = useDeviceChecks();
  // Slice 14c — the human attestations, cross-referenced into the bench by
  // deviceId. This carries the "cleared, not hardened" fact the commissioning
  // response deliberately does not (that response knows only `hardening`), so the
  // two facts stay in two sources and can never be one boolean.
  const clearancesQ = useCommandClearances(isSuperAdmin);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checksFor, setChecksFor] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<CommissionedDevice | null>(null);
  const [clearing, setClearing] = useState<CommissionedDevice | null>(null);
  const [withdrawing, setWithdrawing] = useState<CommissionedDevice | null>(null);

  const devices = useMemo(
    () => (Array.isArray(q.data?.devices) ? q.data.devices : null),
    [q.data],
  );

  const clearanceByDevice = useMemo(
    () =>
      new Map((clearancesQ.data?.clearances ?? []).map((c) => [c.deviceId, c] as const)),
    [clearancesQ.data],
  );

  // 🔴 NO MARKER FOR A DEVICE WITH NO FIX. Filtered here rather than left to the
  // map, so the rule is visible at the screen that owns the claim.
  const markers = useMemo(
    () =>
      (devices ?? [])
        .filter((d) => d.lat !== null && d.lon !== null)
        .map((d) => ({
          id: d.deviceId,
          lat: d.lat,
          lon: d.lon,
          motion: d.motion,
          // Every one of them: a commissioned tracker belongs to no tenant, so
          // nobody on a customer screen can see it.
          ringed: true,
        })),
    [devices],
  );

  if (state.status === 'authenticated' && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  // Loading: a skeleton and NO COUNTS. A "0 trackers" that turns into "7" a
  // moment later is a claim about the warehouse that was never true.
  if (q.isPending) {
    return (
      <section aria-labelledby="commissioning-heading">
        <h1 id="commissioning-heading" className="font-display text-xl font-bold tracking-tight">
          {t('commissioning.title')}
        </h1>
        <div className="mt-4 grid gap-3" role="status" aria-live="polite">
          <span className="sr-only">{t('commissioning.loading')}</span>
          <div className="h-[28rem] animate-pulse rounded-panel border border-raad-border bg-raad-panel" />
          <div className="h-24 animate-pulse rounded-panel border border-raad-border bg-raad-panel" />
        </div>
      </section>
    );
  }

  if (q.isError || devices === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('commissioning.error')}
      </p>
    );
  }

  const degraded = q.data?.degraded === true;
  const nowMs = Date.parse(q.data?.serverTime ?? '') || Date.now();
  const noFix = devices.filter((d) => d.lat === null).length;

  return (
    <section aria-labelledby="commissioning-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="commissioning-heading" className="font-display text-xl font-bold tracking-tight">
          {t('commissioning.title')}
        </h1>
        <p className="text-sm text-raad-muted">
          {t('commissioning.summary', { count: devices.length })}
        </p>
      </div>
      <p className="mt-1 text-[12.5px] text-raad-muted">{t('commissioning.explainer')}</p>

      {/* 🔴 BLIND IS NOT STOPPED. See the header. */}
      {degraded ? (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]"
        >
          {t('commissioning.degraded')}
        </p>
      ) : null}

      {devices.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">
          {t('commissioning.empty')}{' '}
          <Link to="/devices" className="text-raad-accent underline">
            {t('commissioning.emptyLink')}
          </Link>
        </p>
      ) : (
        <>
          <Suspense
            fallback={
              <div className="mt-4 flex h-[28rem] items-center justify-center rounded-panel border border-raad-border bg-raad-panel">
                <span className="text-sm text-raad-muted" role="status">
                  {t('live.mapLoading')}
                </span>
              </div>
            }
          >
            <div className="mt-4">
              <GlobalMap markers={markers} selectedId={selectedId} onSelect={setSelectedId} />
            </div>
          </Suspense>

          {noFix > 0 ? (
            <p className="mt-2 text-[11.5px] text-raad-muted">
              {t('commissioning.noFixOnMap', { count: noFix })}
            </p>
          ) : null}

          <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('inventory.devices.imei')}
                  </th>
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('inventory.devices.sim')}
                  </th>
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('commissioning.col.lastFix')}
                  </th>
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('commissioning.col.motion')}
                  </th>
                  <th scope="col" className="pb-2 pr-4 font-medium">
                    {t('commissioning.col.hardening')}
                  </th>
                  {/* Slice 14c — a SEPARATE column from SMS lock. Hardening is a
                      machine proof; clearance is a human's attestation, and the
                      two never share a cell. */}
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('clearance.col.header')}
                  </th>
                  {/* 🔴 SLICE 14e-fix — THE INTERLOCK COLUMN WAS REMOVED FROM
                      THIS BENCH. The bench lists only `devices_commissioned`
                      (trackers with no open assignment — not yet given to a
                      customer), so its interlock control was structurally
                      unreachable for every INSTALLED device, which is the only
                      kind whose interlock matters. It now lives on the device
                      register, which selects `FROM devices` and lists installed
                      hardware too. The header/cell count test below still holds
                      at seven columns. */}
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('inventory.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => {
                  // Bound OUTSIDE the JSX so TypeScript narrows it — a `!` in
                  // the branch below would be an assertion where a fact is
                  // available.
                  const fixTime = d.fixTime;
                  const days =
                    d.commissionedAt === null ? null : daysSince(d.commissionedAt, nowMs);
                  return (
                    <tr
                      key={d.deviceId}
                      className={[
                        'border-b border-raad-border last:border-b-0',
                        d.deviceId === selectedId ? 'bg-raad-primary/10' : '',
                      ].join(' ')}
                    >
                      <td className="py-3 pr-4 align-top">
                        <button
                          type="button"
                          onClick={() => setSelectedId(d.deviceId)}
                          aria-pressed={d.deviceId === selectedId}
                          className="text-left"
                        >
                          <span className="num font-medium">{d.imei}</span>
                        </button>
                        {d.model ? (
                          <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                            {d.model}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 align-top">
                        {d.simIccid === null ? (
                          <span className="text-raad-muted">{t('inventory.devices.noSim')}</span>
                        ) : (
                          <>
                            <span className="num">{d.simIccid}</span>
                            <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                              {d.simMsisdn === null ? (
                                <span className="text-raad-warn">
                                  {t('inventory.sims.notActivated')}
                                </span>
                              ) : (
                                <span className="num">{d.simMsisdn}</span>
                              )}
                              {d.simCarrier ? ` · ${d.simCarrier}` : ''}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top">
                        {fixTime !== null ? (
                          <span className="num">{formatInstant(fixTime, i18n.language)}</span>
                        ) : (
                          <>
                            {/* An em dash, and then the reason. A blank cell
                                reads as a bug; "no position yet" reads as
                                waiting, which is what it is. */}
                            <span aria-hidden>—</span>
                            <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                              {/* "Commissioned 0 days ago" is not a sentence,
                                  and i18next's `_one`/`_other` cannot say
                                  "today" — so today gets its own key rather
                                  than a plural rule that does not exist. */}
                              {days === null
                                ? t('commissioning.noFixEver')
                                : days === 0
                                  ? t('commissioning.noFixToday')
                                  : t('commissioning.noFixSince', { count: days })}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top">
                        {degraded ? (
                          // 🔴 NEVER "stopped" WHILE BLIND. The banner above says
                          // why; this cell must not contradict it.
                          <span className="text-raad-muted">{t('commissioning.motionBlind')}</span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <span
                              className={[
                                'size-2 flex-none rounded-full',
                                MOTION_DOT[d.motion],
                              ].join(' ')}
                            />
                            {t(`live.motion.${d.motion}`)}
                            {d.speedKmh !== null && d.motion === 'moving'
                              ? ` · ${String(Math.round(d.speedKmh))} km/h`
                              : ''}
                          </span>
                        )}
                      </td>
                      {/* Slice 14a — does this tracker still answer to the shared
                          credential? The operator acts on this column, so it sits
                          beside motion rather than behind a dialog. */}
                      <td className="py-3 pr-4 align-top">
                        <HardeningCell state={d.hardening} />
                      </td>
                      {/* Slice 14c — proven vs cleared-by-a-human, distinctly. */}
                      <td className="py-3 pr-4 align-top">
                        <ClearanceCell
                          hardening={d.hardening}
                          clearance={clearanceByDevice.get(d.deviceId)}
                          language={i18n.language}
                          onClear={() => setClearing(d)}
                          onWithdraw={() => setWithdrawing(d)}
                        />
                      </td>
                      <td className="py-3 pr-4 align-top">
                        <span className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={SECONDARY_BUTTON}
                            disabled={checks.isPending && checksFor === d.deviceId}
                            onClick={() => {
                              setChecksFor(d.deviceId);
                              checks.mutate(d.deviceId);
                            }}
                          >
                            {checks.isPending && checksFor === d.deviceId
                              ? t('provision.checking')
                              : t('commissioning.runChecks')}
                          </button>
                          <button
                            type="button"
                            className={SECONDARY_BUTTON}
                            onClick={() => setAssigning(d)}
                          >
                            {t('commissioning.assign')}
                          </button>
                        </span>
                        {/* Inline, under the row that was asked about — a
                            dialog would hide the list the operator is
                            comparing against. */}
                        {checksFor === d.deviceId && checks.data?.deviceId === d.deviceId ? (
                          <ul className="mt-2 rounded-xl border border-raad-border px-3">
                            {checks.data.checks.map((c) => (
                              <CheckRow key={c.id} check={c} />
                            ))}
                          </ul>
                        ) : null}
                        {checksFor === d.deviceId && checks.isError ? (
                          <p role="alert" className="mt-2 text-[12px] text-raad-error">
                            {t('golive.checksError')}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {assigning !== null ? (
        <AssignDeviceDialog
          device={{
            deviceId: assigning.deviceId,
            imei: assigning.imei,
            model: assigning.model,
            simIccid: assigning.simIccid,
            simMsisdn: assigning.simMsisdn,
            simCarrier: assigning.simCarrier,
          }}
          onClose={() => setAssigning(null)}
        />
      ) : null}

      {clearing !== null ? (
        <ClearForCommandingDialog
          device={{ deviceId: clearing.deviceId, imei: clearing.imei }}
          onClose={() => setClearing(null)}
        />
      ) : null}

      {withdrawing !== null ? (
        <WithdrawClearanceDialog
          device={{ deviceId: withdrawing.deviceId, imei: withdrawing.imei }}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
    </section>
  );
}

export default Commissioning;
